import { CalendarDate, getLocalTimeZone } from '@internationalized/date';
import { useCallback, useEffect, useState } from 'react';
import DatePicker from './DatePicker';
import { DateValue } from '@react-aria/calendar';

export const DATE_LOCALE = 'en-GB';

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

interface MultiDateRangePickerProps {
  label?: string;
  locale?: string;
  minDate?: Date;
  maxDate?: Date;
  maxRangeInMonths?: number;
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const toDateValue = (d?: Date | null): CalendarDate | null =>
  d ? new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate()) : null;

const isWithinMaxRange = (startDate: Date, endDate: Date, maxRangeInMonths?: number) => {
  if (!maxRangeInMonths) {
    return true;
  }

  const latestAllowedEnd = new Date(startDate);
  latestAllowedEnd.setMonth(latestAllowedEnd.getMonth() + maxRangeInMonths);

  return endDate <= latestAllowedEnd;
};

const isCommitReadyRange = (
  startDate: Date | null,
  endDate: Date | null,
  minDate?: Date,
  maxDate?: Date,
  maxRangeInMonths?: number,
) => {
  if (!startDate || !endDate) {
    return true;
  }

  if (startDate > endDate) {
    return false;
  }

  if (minDate && (startDate < minDate || endDate < minDate)) {
    return false;
  }

  if (maxDate && (startDate > maxDate || endDate > maxDate)) {
    return false;
  }

  return isWithinMaxRange(startDate, endDate, maxRangeInMonths);
};

const MultiDateRangePicker = ({
  label = 'Select period:',
  locale = DATE_LOCALE,
  minDate,
  maxDate,
  maxRangeInMonths,
  value,
  onChange,
}: MultiDateRangePickerProps) => {
  const [draftRange, setDraftRange] = useState(value);
  const { startDate, endDate } = draftRange;

  useEffect(() => {
    setDraftRange(value);
  }, [value]);

  const isWithinBounds = useCallback(
    (date: Date) => {
      if (minDate && date < minDate) {
        return false;
      }

      if (maxDate && date > maxDate) {
        return false;
      }

      return true;
    },
    [maxDate, minDate],
  );

  const areDatesWithinBounds =
    (!startDate || isWithinBounds(startDate)) && (!endDate || isWithinBounds(endDate));

  let validationMessage: string | undefined;

  if (startDate && endDate && areDatesWithinBounds && startDate > endDate) {
    validationMessage = 'End date must be on or after start date.';
  } else if (
    startDate &&
    endDate &&
    areDatesWithinBounds &&
    maxRangeInMonths &&
    !isWithinMaxRange(startDate, endDate, maxRangeInMonths)
  ) {
    validationMessage = `Select a period of ${maxRangeInMonths} months or less.`;
  }

  const handleStartChange = useCallback(
    (calendarValue: DateValue | null) => {
      if (!calendarValue) {
        const nextRange = { ...draftRange, startDate: null };
        setDraftRange(nextRange);
        onChange(nextRange);
        return;
      }

      const newStart = calendarValue.toDate(getLocalTimeZone());

      const nextRange = {
        ...draftRange,
        startDate: newStart,
      };

      setDraftRange(nextRange);

      if (
        isWithinBounds(newStart) &&
        isCommitReadyRange(newStart, nextRange.endDate, minDate, maxDate, maxRangeInMonths)
      ) {
        onChange(nextRange);
      }
    },
    [draftRange, isWithinBounds, maxDate, maxRangeInMonths, minDate, onChange],
  );

  const handleEndChange = useCallback(
    (calendarValue: DateValue | null) => {
      if (!calendarValue) {
        const nextRange = { ...draftRange, endDate: null };
        setDraftRange(nextRange);
        onChange(nextRange);
        return;
      }

      const newEnd = calendarValue.toDate(getLocalTimeZone());

      const nextRange = {
        ...draftRange,
        endDate: newEnd,
      };

      setDraftRange(nextRange);

      if (
        isWithinBounds(newEnd) &&
        isCommitReadyRange(nextRange.startDate, newEnd, minDate, maxDate, maxRangeInMonths)
      ) {
        onChange(nextRange);
      }
    },
    [draftRange, isWithinBounds, maxDate, maxRangeInMonths, minDate, onChange],
  );

  return (
    <div className="space-y-2">
      <div className="flex gap-4 items-end">
        <div>
          <DatePicker
            label={label}
            locale={locale}
            minValue={toDateValue(minDate)}
            maxValue={toDateValue(maxDate)}
            value={toDateValue(startDate)}
            onChange={handleStartChange}
          />
        </div>

        <DatePicker
          label=""
          locale={locale}
          minValue={toDateValue(minDate)}
          maxValue={toDateValue(maxDate)}
          value={toDateValue(endDate)}
          onChange={handleEndChange}
        />
      </div>
      {validationMessage && <p className="text-sm text-red-600">{validationMessage}</p>}
    </div>
  );
};

export default MultiDateRangePicker;
